import { ChangeDetectionStrategy, Component, computed, inject, input, model } from "@angular/core";
import { DividerModule, IconModule, UiKitToasterService } from "@jack-sparrow-libraries/ui-kit";
import { MatIconModule } from "@angular/material/icon";
import { SideNavActionsMenuPartItem } from "@core/layout/components/side-nav/components/side-nav-actions-menu/models/side-nav-actions-menu.model";
import { NavigationService } from "@services/navigation.service";
import { animate, group, query, stagger, style, transition, trigger } from "@angular/animations";
import { SideNavActionsMenuService } from "@core/layout/components/side-nav/components/side-nav-actions-menu/services/side-nav-actions-menu.service";
import { FullClient } from "@models/full-client.model";
import { LinkUtils } from "@core/layout/utils/link.utils";
import { EmotionEventsHelperService, StrutsActionType } from "@services/helpers/emotion-events-helper.service";
import { SideNavActionsIds } from "@core/layout/components/side-nav/components/side-nav-actions-menu/enums/side-nav-actions.ids";
import { LayoutService } from "@core/layout/services/layout.service";

type OnActionMenuAddPayload = {
	id: SideNavActionsIds;
	params: { [key: string]: unknown };
	samePage: boolean;
	haveAction: boolean;
};

@Component({
	selector: "cba-side-nav-actions-menu",
	templateUrl: "./side-nav-actions-menu.component.html",
	styleUrls: ["./side-nav-actions-menu.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [DividerModule, MatIconModule, IconModule],
	animations: [
		trigger("menuAnimation", [
			transition(":enter", [
				query(".column-item", [style({ opacity: 0, transform: "translateX(-10px)" })]),
				style({
					opacity: 0,
					transform: "scaleX(0) scaleY(0)",
					transformOrigin: "left 50px",
				}),
				group([
					animate(
						"200ms cubic-bezier(0.4, 0, 0.2, 1)",
						style({
							opacity: 1,
							transform: "scaleX(1) scaleY(1)",
						}),
					),
					query(".column-item", [
						style({ opacity: 0, transform: "translateX(-10px)" }),
						stagger(100, [
							animate(
								"300ms cubic-bezier(0.4, 0, 0.2, 1)",
								style({ opacity: 1, transform: "translateX(0)" }),
							),
						]),
					]),
				]),
			]),
			transition(":leave", [
				style({
					opacity: 1,
					transform: "scaleX(1) scaleY(1)",
				}),
				animate(
					"150ms cubic-bezier(0.4, 0, 0.2, 1)",
					style({
						opacity: 0,
						transform: "translateX(-20px) scale(0.95)",
					}),
				),
			]),
		]),
	],
})
export class SideNavActionsMenuComponent {
	private readonly navigationService = inject(NavigationService);
	private readonly sideNavActionsMenuService = inject(SideNavActionsMenuService);
	private readonly layoutService = inject(LayoutService);
	private readonly toasterService = inject(UiKitToasterService);

	readonly $fullClient = input.required<FullClient>({ alias: "fullClient" });

	readonly $opened = model.required<boolean>({ alias: "opened" });

	readonly $columns = computed(() => this.sideNavActionsMenuService.generateItems(this.$fullClient()));

	async onAction(item: SideNavActionsMenuPartItem) {
		if ("link" in item && item.link) {
			let link = await this.checkForSelectPatient(item.link);

			if (!link) {
				return;
			}

			const newUrl = new URL(window.location.origin + link);
			const payload = this.getActionPayload(newUrl, item);

			if (!payload.samePage && payload.haveAction) {
				this.sendAction(payload);
			}

			if (payload.haveAction) {
				const redirectUrl = new URL(newUrl);
				redirectUrl.searchParams.delete("action");
				link = redirectUrl.toString().replace(redirectUrl.origin, "");
			}

			await this.navigationService.navigateByUrl(link);

			if (payload.samePage && payload.haveAction) {
				this.sendAction(payload);
			}
		} else if ("action" in item && item.action) {
			item.action();
		} else {
			console.log(`Action ${item.id} is not implemented yet.`);
			this.toasterService.show({
				color: "alert",
				titre: "Cette fonctionnalité n'est pas encore disponible",
			});
		}
	}

	private async checkForSelectPatient(link: string) {
		if (link.includes(":idPatient")) {
			const idPatient = await this.layoutService.selectPatient();
			if (!idPatient) {
				return;
			}
			link = LinkUtils.replaceUrlParams(link, { idPatient });
		}

		return link;
	}

	private getActionPayload(newUrl: URL, item: SideNavActionsMenuPartItem): OnActionMenuAddPayload {
		const previousUrl = new URL(window.location.href);

		const samePage = newUrl.pathname === previousUrl.pathname && newUrl.hash === previousUrl.hash;
		const haveAction = !!newUrl.searchParams.size && !!newUrl.searchParams.get("action");

		return {
			id: item.id,
			params: Object.fromEntries(newUrl.searchParams.entries()),
			samePage,
			haveAction,
		};
	}

	private sendAction(payload: OnActionMenuAddPayload) {
		EmotionEventsHelperService.dispatchEvent({
			action: StrutsActionType.ON_MENU_ACTIONS_ADD,
			payload,
		});
	}
}
